import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import Pill from '@/app/common/components/Pill/index.tsx';
import {
  ChipList,
  ChipsWithToggle,
  GroupContainer,
  GroupTitle,
  ShowMoreButton,
  ShowMoreLabel,
} from '@/app/common/components/ChipSelect/styled.ts';
import ArrowIcon from '@/app/components/search/common/Icons/ArrowIcon';
import type { ChipOption } from '@/app/common/components/ChipSelect/types.ts';

export type ChipSelectGroupProps = {
  title?: ReactNode;
  options: ChipOption[];
  onToggle: (id: string) => void;
  maxVisible?: number;
  showMoreLabel?: (hiddenCount: number) => string;
  showLessLabel?: string;
  disabled?: boolean;
  busy?: boolean;
  className?: string;
};

const ChipSelectGroup = ({
  title,
  options,
  onToggle,
  maxVisible,
  showMoreLabel,
  showLessLabel,
  disabled,
  busy,
  className,
}: ChipSelectGroupProps) => {
  const titleId = useId();
  const listId = useId();
  const [expandedByUser, setExpandedByUser] = useState<boolean | null>(null);

  const hiddenWhenCollapsed = maxVisible === undefined ? 0 : Math.max(options.length - maxVisible, 0);
  const expandLabel = hiddenWhenCollapsed ? showMoreLabel?.(hiddenWhenCollapsed) : undefined;
  const canCollapse = !!expandLabel && !!showLessLabel;
  const hiddenHoldsSelection = canCollapse && options.slice(maxVisible).some((option) => option.selected);
  const showAll = !canCollapse || (expandedByUser ?? hiddenHoldsSelection);

  useEffect(() => {
    if (hiddenHoldsSelection) setExpandedByUser((expanded) => expanded || null);
  }, [hiddenHoldsSelection]);

  const visibleOptions = showAll ? options : options.slice(0, maxVisible);
  const toggleLabel = showAll ? showLessLabel : expandLabel;

  if (!options.length) return null;

  return (
    <GroupContainer className={className}>
      {title && <GroupTitle id={titleId}>{title}</GroupTitle>}
      <ChipsWithToggle>
        <ChipList
          id={listId}
          role={title ? 'group' : undefined}
          aria-labelledby={title ? titleId : undefined}
          aria-busy={busy || undefined}
        >
          {visibleOptions.map((option) => (
            <Pill
              key={option.id}
              label={option.label}
              selected={option.selected}
              pressed={!!option.selected}
              busy={disabled || busy || (option.disabled && !option.selected)}
              isLoading={false}
              isOpen={false}
              showArrow={false}
              handleClick={() => onToggle(option.id)}
            />
          ))}
        </ChipList>
        {canCollapse && (
          <ShowMoreButton
            type="button"
            $expanded={showAll}
            aria-expanded={showAll}
            aria-controls={listId}
            onClick={() => setExpandedByUser(!showAll)}
          >
            <ShowMoreLabel>{toggleLabel}</ShowMoreLabel>
            <ArrowIcon />
          </ShowMoreButton>
        )}
      </ChipsWithToggle>
    </GroupContainer>
  );
};

export default ChipSelectGroup;
